import { useState, useMemo, useEffect } from 'react';
import { Plus, Building2, Search, DollarSign, Eye, EyeOff, X, TrendingUp, CreditCard, Wallet, Briefcase } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTranslation } from 'react-i18next';
import AddAccount from '@/components/accounts/AddAccount';
import AccountCard from '@/components/accounts/AccountCard';
import { useAccounts, useAccountsStore } from '@/store/accountsStore';
import { useAccountsStore as useCoreAccountsStore } from '@flows.cash/core';
import { useCurrencyConversion } from '@/hooks/useCurrencyConversion';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAuthStore } from '@/store/authStore';
import type { Account } from '@/types/base';

type SortOption = 'name' | 'balance' | 'type';
type FilterType = 'all' | 'bank' | 'credit' | 'cash' | 'investment' | 'loan';

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

// Account type configurations
const accountTypeConfig = {
  checking: { 
    icon: Building2, 
    label: 'Checking', 
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20' 
  },
  savings: { 
    icon: Building2, 
    label: 'Savings', 
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-900/20' 
  },
  credit: { 
    icon: CreditCard, 
    label: 'Credit', 
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-50 dark:bg-purple-900/20' 
  },
  cash: { 
    icon: Wallet, 
    label: 'Cash', 
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-900/20' 
  },
  investment: { 
    icon: Briefcase, 
    label: 'Investment', 
    color: 'text-indigo-600 dark:text-indigo-400',
    bgColor: 'bg-indigo-50 dark:bg-indigo-900/20' 
  },
  loan: { 
    icon: Building2, 
    label: 'Loan', 
    color: 'text-yellow-600 dark:text-yellow-400',
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/20' 
  }
};

export default function Accounts() {
  const { t } = useTranslation();
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('name');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [showArchived, setShowArchived] = useState(false);
  const [accountStats, setAccountStats] = useState({
    totalBalance: 0,
    bankBalance: 0,
    creditBalance: 0,
    cashBalance: 0,
    investmentBalance: 0,
    totalAccounts: 0,
    activeAccounts: 0,
    loading: true
  });
  
  // Get accounts data with server-calculated balances from account_balances_view
  const { accounts: rawAccounts, loading, error } = useAccounts();
  const accounts = rawAccounts as unknown as Account[];
  
  const { formatAmount, defaultCurrency } = useCurrencyConversion();
  const { user } = useAuthStore();
  const { refreshAllBalances } = useAccountsStore();

  // Load stats from database view on mount
  useEffect(() => {
    const loadAccountStats = async () => {
      if (!user?.id) return;
      
      try {
        setAccountStats(prev => ({ ...prev, loading: true }));
        
        // Load total balance from server-side aggregation (account_income_expenses view)
        const totalBalanceData = await useCoreAccountsStore.getState().fetchTotalBalance(user.id);
        
        if (totalBalanceData) {
          // Only count active accounts - all balances come from server-side views
          const activeAccounts = accounts.filter(account => !account.isArchived).length;
          
          setAccountStats({
            totalBalance: totalBalanceData.total,
            bankBalance: 0, // Will be hidden or simplified in UI
            creditBalance: 0, // Will be hidden or simplified in UI
            cashBalance: 0, // Will be hidden or simplified in UI
            investmentBalance: 0, // Will be hidden or simplified in UI
            totalAccounts: accounts.length,
            activeAccounts: activeAccounts,
            loading: false
          });
        } else {
          // Fallback to simple count if no data
          setAccountStats({
            totalBalance: 0,
            bankBalance: 0,
            creditBalance: 0,
            cashBalance: 0,
            investmentBalance: 0,
            totalAccounts: accounts.length,
            activeAccounts: accounts.filter(a => !a.isArchived).length,
            loading: false
          });
        }
      } catch (error) {
        console.error('Error loading account stats:', error);
        setAccountStats(prev => ({ ...prev, loading: false }));
      }
    };

    if (user?.id) {
      loadAccountStats();
    }
  }, [user?.id]);

  // Update account counts when accounts change (without refetching from database)
  useEffect(() => {
    if (accounts.length >= 0) {
      const activeAccounts = accounts.filter(account => !account.isArchived).length;
      
      setAccountStats(prev => ({
        ...prev,
        totalAccounts: accounts.length,
        activeAccounts: activeAccounts
      }));
    }
  }, [accounts]);

  // Server-Side Balance Strategy:
  // - Individual account balances: Loaded from account_balances_view.current_balance
  // - Total balances: Aggregated server-side via account_income_expenses view
  // - NO client-side balance calculations - all data from database views

  // Count archived accounts
  const archivedCount = useMemo(() => {
    return accounts.filter(account => account.isArchived).length;
  }, [accounts]);

  // Filter and sort accounts
  const filteredAndSortedAccounts = useMemo(() => {
    if (!accounts?.length) return [];
    
    let filtered = accounts.filter(account => {
      const matchesSearch = searchQuery ? 
        account.name.toLowerCase().includes(searchQuery.toLowerCase()) : true;
      const matchesType = filterType === 'all' || 
        account.type === filterType || 
        (filterType === 'bank' && (account.type === 'checking' || account.type === 'savings'));
      const matchesArchiveFilter = showArchived || !account.isArchived;
      
      return matchesSearch && matchesType && matchesArchiveFilter;
    });

    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'balance':
          return (b.balance || 0) - (a.balance || 0);
        case 'type':
          return (a.type || '').localeCompare(b.type || '');
        default:
          return 0;
      }
    });
  }, [accounts, searchQuery, sortBy, filterType, showArchived]);

  // Group accounts by type for display
  const accountsByType = useMemo(() => {
    const groups: Record<string, Account[]> = {};
    
    filteredAndSortedAccounts.forEach(account => {
      // Map 'bank' type to 'checking' for display consistency
      const displayType = account.type === 'bank' ? 'checking' : account.type;
      
      if (!groups[displayType]) {
        groups[displayType] = [];
      }
      groups[displayType].push(account);
    });
    
    return groups;
  }, [filteredAndSortedAccounts]);

  const renderAccountGroup = (type: string, accounts: Account[]) => {
    const config = accountTypeConfig[type as keyof typeof accountTypeConfig];
    if (!config || accounts.length === 0) return null;

    const Icon = config.icon;

    return (
      <motion.div 
        key={type}
        variants={item} 
        className="space-y-4"
      >
        <div className="flex items-center gap-3">
          <div className={cn(
            "w-10 h-10 rounded-xl flex items-center justify-center",
            config.bgColor
          )}>
            <Icon className={cn("w-5 h-5", config.color)} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
              {config.label}
            </h2>
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
              {accounts.length} {accounts.length === 1 ? 'account' : 'accounts'}
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {accounts.map((account) => (
            <motion.div key={account.id} variants={item}>
              <AccountCard account={account} />
            </motion.div>
          ))}
        </div>
      </motion.div>
    );
  };

  if (loading && !accounts.length) {
    return (
      <div className="flex-1 bg-background-primary dark:bg-background-dark-primary">
        <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-secondary/10 dark:bg-primary-dark/10 flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-secondary dark:text-primary-dark animate-pulse" />
            </div>
            <p className="text-text-secondary dark:text-text-dark-secondary">
              Loading accounts...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-background-primary dark:bg-background-dark-primary">
      <div className="h-full flex flex-col p-4 sm:p-6 space-y-6">
        {/* Header */}
        <div className="border border-border dark:border-border-dark bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 rounded-2xl shadow-sm">
          <div className="p-6">
            {/* Title Section */}
            <div className="flex flex-col sm:flex-row gap-6 sm:items-center justify-between mb-8">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-secondary/20 to-secondary/10 dark:from-primary-dark/20 dark:to-primary-dark/10 flex items-center justify-center shadow-inner">
                  <Building2 className="w-7 h-7 text-secondary dark:text-primary-dark" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent">
                    {t('accounts.title')}
                  </h1>
                  <p className="text-text-secondary dark:text-text-dark-secondary mt-1">
                    Manage your financial accounts
                  </p>
                </div>
              </div>
              
              <Button
                onClick={() => setShowAddAccount(true)}
                className="bg-primary hover:bg-primary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 text-white transition-all duration-200 h-11 px-6 rounded-xl shadow-sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Account
              </Button>
            </div>

            {/* Statistics Cards */}
            <div className="grid gap-4 md:grid-cols-3 mb-8">
              <Card className="bg-gradient-to-br from-background-secondary to-background-secondary/50 dark:from-background-dark-secondary dark:to-background-dark-secondary/50 border-border dark:border-border-dark backdrop-blur-xl rounded-xl overflow-hidden group hover:shadow-lg transition-all duration-200">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                        Total Balance
                      </p>
                      {accountStats.loading ? (
                        <div className="w-24 h-6 bg-background-primary/50 dark:bg-background-dark-primary/50 rounded animate-pulse mt-1"></div>
                      ) : (
                        <h3 className="text-xl sm:text-2xl font-bold text-text-primary dark:text-text-dark-primary mt-1">
                          {formatAmount(accountStats.totalBalance, defaultCurrency)}
                        </h3>
                      )}
                      <p className="text-xs text-text-secondary dark:text-text-dark-secondary mt-1">
                        From database view
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-green-50 dark:bg-green-900/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                      <TrendingUp className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-background-secondary to-background-secondary/50 dark:from-background-dark-secondary dark:to-background-dark-secondary/50 border-border dark:border-border-dark backdrop-blur-xl rounded-xl overflow-hidden group hover:shadow-lg transition-all duration-200">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                        Active Accounts
                      </p>
                      {accountStats.loading ? (
                        <div className="w-16 h-6 bg-background-primary/50 dark:bg-background-dark-primary/50 rounded animate-pulse mt-1"></div>
                      ) : (
                        <h3 className="text-xl sm:text-2xl font-bold text-text-primary dark:text-text-dark-primary mt-1">
                          {accountStats.activeAccounts}
                        </h3>
                      )}
                      <p className="text-xs text-text-secondary dark:text-text-dark-secondary mt-1">
                        {accountStats.totalAccounts} total accounts
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                      <Building2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-background-secondary to-background-secondary/50 dark:from-background-dark-secondary dark:to-background-dark-secondary/50 border-border dark:border-border-dark backdrop-blur-xl rounded-xl overflow-hidden group hover:shadow-lg transition-all duration-200">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                        Account Types
                      </p>
                      {accountStats.loading ? (
                        <div className="w-16 h-6 bg-background-primary/50 dark:bg-background-dark-primary/50 rounded animate-pulse mt-1"></div>
                      ) : (
                        <h3 className="text-xl sm:text-2xl font-bold text-text-primary dark:text-text-dark-primary mt-1">
                          {Object.keys(accountsByType).length}
                        </h3>
                      )}
                      <p className="text-xs text-text-secondary dark:text-text-dark-secondary mt-1">
                        Different types
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                      <Wallet className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Filters Section */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex-1 w-full sm:w-auto relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-secondary dark:text-text-dark-secondary" />
                <Input
                  placeholder="Search accounts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-background-secondary/90 dark:bg-background-dark-secondary/90 border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark rounded-xl h-11 w-full"
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <Select value={filterType} onValueChange={(value: FilterType) => setFilterType(value)}>
                <SelectTrigger className="w-full sm:w-[180px] bg-background-secondary/90 dark:bg-background-dark-secondary/90 border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark rounded-xl h-11">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent className="bg-background-secondary/90 dark:bg-background-dark-secondary/90 border-border dark:border-border-dark backdrop-blur-xl">
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="bank">Bank Accounts</SelectItem>
                  <SelectItem value="credit">Credit Cards</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="investment">Investment</SelectItem>
                  <SelectItem value="loan">Loan</SelectItem>
                </SelectContent>
              </Select>

              <Select value={sortBy} onValueChange={(value: SortOption) => setSortBy(value)}>
                <SelectTrigger className="w-full sm:w-[180px] bg-background-secondary/90 dark:bg-background-dark-secondary/90 border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark rounded-xl h-11">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent className="bg-background-secondary/90 dark:bg-background-dark-secondary/90 border-border dark:border-border-dark backdrop-blur-xl">
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="balance">Balance</SelectItem>
                  <SelectItem value="type">Type</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Archive toggle */}
            {archivedCount > 0 && (
              <div className="mt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowArchived(!showArchived)}
                  className={cn(
                    "h-9 px-4 text-sm rounded-xl transition-all duration-200",
                    showArchived 
                      ? "bg-secondary/10 text-secondary dark:bg-primary-dark/10 dark:text-primary-dark border-secondary dark:border-primary-dark hover:bg-secondary/20 dark:hover:bg-primary-dark/20" 
                      : "bg-background-secondary/90 dark:bg-background-dark-secondary/90 text-text-secondary dark:text-text-dark-secondary border-border dark:border-border-dark hover:bg-border/5 dark:hover:bg-border-dark/5"
                  )}
                >
                  {showArchived ? (
                    <>
                      <EyeOff className="w-4 h-4 mr-2" />
                      Hide Archived ({archivedCount})
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4 mr-2" />
                      Show Archived ({archivedCount})
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Accounts List */}
        <ScrollArea className="flex-1">
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="space-y-8 pb-6"
          >
            {filteredAndSortedAccounts.length === 0 ? (
              <motion.div 
                className="flex flex-col items-center justify-center py-16 text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="w-20 h-20 rounded-xl bg-secondary/10 dark:bg-primary-dark/10 flex items-center justify-center mb-6">
                  {searchQuery ? (
                    <Search className="w-10 h-10 text-secondary dark:text-primary-dark" />
                  ) : (
                    <Building2 className="w-10 h-10 text-secondary dark:text-primary-dark" />
                  )}
                </div>
                <h3 className="text-2xl font-semibold text-text-primary dark:text-text-dark-primary">
                  {searchQuery 
                    ? 'No accounts found'
                    : 'No accounts yet'
                  }
                </h3>
                <p className="text-text-secondary dark:text-text-dark-secondary mt-3 max-w-md mx-auto">
                  {searchQuery 
                    ? 'Try adjusting your search or filters'
                    : 'Get started by adding your first account'
                  }
                </p>
                {!searchQuery && (
                  <Button
                    onClick={() => setShowAddAccount(true)}
                    className="mt-8 bg-primary hover:bg-primary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 text-white transition-all duration-200 h-11 px-6 rounded-xl shadow-sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Account
                  </Button>
                )}
              </motion.div>
            ) : (
              Object.entries(accountsByType).map(([type, accounts]) => 
                renderAccountGroup(type, accounts)
              )
            )}
          </motion.div>
        </ScrollArea>

        <AddAccount 
          open={showAddAccount} 
          onClose={() => setShowAddAccount(false)} 
        />
      </div>
    </div>
  );
}